import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {DISPLAY_URL, SETTINGS_URL, settingsUrl} from "../services/categoryProfileUrls";
import type {CategorySettings, SettingValue} from "../types/settingsTypes";
import {done, failed} from "./notices";

export function useCategorySettingsQuery(enabled: boolean) {
    return useCustomQuery({
        queryKey: [SETTINGS_URL] as const,
        queryFn: async () => (await queryHttp.get<CategorySettings>(SETTINGS_URL)).data,
        enabled,
        retry: false,
    });
}

/** Field errors from a refused settings change (`{errors: {key: message}}`), if any. */
export function fieldErrors(e: unknown): Record<string, string> | null {
    const data = (e as {response?: {data?: {errors?: Record<string, string>}}})?.response?.data;
    return data?.errors ?? null;
}

/** Settings writes; each merges the server's answer into the page's query. */
export function useCategorySettingsActions() {
    const qc = useQueryClient();
    return useMemo(() => {
        const merge = (part: Partial<CategorySettings>) => {
            qc.setQueryData<CategorySettings>([SETTINGS_URL], old => old ? {...old, ...part} : old);
            // The Display settings (card, hidden tabs) follow at once
            void qc.invalidateQueries({queryKey: [DISPLAY_URL]});
        };

        return {
            /** Save changes (null = back to the default). Rejects with the field errors for the form to show. */
            save: async (changes: Record<string, SettingValue | null>, previous?: Record<string, SettingValue>) => {
                const res = await queryHttp.patch<Partial<CategorySettings>>(settingsUrl('values'), {changes});
                merge(res.data);
                done(Object.values(changes).every(v => v === null) ? 'Back to the default.' : 'Settings saved.',
                    previous ? async () => {
                        merge((await queryHttp.patch<Partial<CategorySettings>>(settingsUrl('values'), {changes: previous})).data);
                    } : undefined);
            },
            setSeriesEnabled: async (id: number, name: string, enabled: boolean) => {
                try {
                    const write = async (on: boolean) =>
                        merge((await queryHttp.patch<Partial<CategorySettings>>(settingsUrl(`series/${id}`), {enabled: on})).data);
                    await write(enabled);
                    done(`${name} ${enabled ? 'switched on' : 'switched off'}.`, () => write(!enabled));
                } catch (e) {
                    failed(e);
                }
            },
            ingest: async (provider: string | null) => {
                try {
                    const res = await queryHttp.post<Partial<CategorySettings> & {outcome: Record<string, string>}>(
                        settingsUrl('ingest'), {provider});
                    merge(res.data);
                    const outcomes = Object.values(res.data.outcome ?? {});
                    const failedCount = outcomes.filter(o => o.startsWith('failed')).length;
                    done(failedCount ? `Refreshed, ${failedCount} series failed (see the list).` : 'Market data refreshed.');
                } catch (e) {
                    failed(e);
                }
            },
            rebuild: async (databagId: number, name: string) => {
                try {
                    merge((await queryHttp.post<Partial<CategorySettings>>(settingsUrl(`builds/${databagId}`))).data);
                    done(`${name}: facts and benchmarks rebuilt.`);
                } catch (e) {
                    failed(e);
                }
            },
        };
    }, [qc]);
}
